import { Fragment, useMemo, type CSSProperties } from "react";
import { parseMarkup, type BlockNode, type InlineNode } from "./markup.js";

/**
 * Renders the `markup.ts` subset as React elements.
 *
 * There is no `dangerouslySetInnerHTML` here and there must never be one: the whole point of
 * parsing to an AST is that staff-authored copy reaches an applicant's browser as elements this
 * component chose, not as a string a sanitiser had to be trusted to clean.
 *
 * Sizes are `em`-relative so the same component reads correctly inside the terms field's small
 * scroll box and inside a full-width rich-text block, without either caller passing overrides.
 */

// Levels start at h3: this copy is nested inside a form that already owns h1/h2, and skipping
// heading levels is a WCAG 2.1 AA failure (1.3.1) even when it looks identical.
const HEADING_TAG: Record<1 | 2 | 3, "h3" | "h4" | "h5"> = { 1: "h3", 2: "h4", 3: "h5" };

const HEADING_CLASS: Record<1 | 2 | 3, string> = {
  1: "text-[1.35em] font-semibold",
  2: "text-[1.15em] font-semibold",
  3: "text-[1em] font-semibold",
};

const renderInline = (nodes: readonly InlineNode[]): JSX.Element => (
  <>
    {nodes.map((node, index) => {
      switch (node.kind) {
        case "text":
          return <Fragment key={index}>{node.text}</Fragment>;
        case "break":
          return <br key={index} />;
        case "strong":
          return (
            <strong key={index} className="font-semibold">
              {renderInline(node.children)}
            </strong>
          );
        case "em":
          return <em key={index}>{renderInline(node.children)}</em>;
        case "link":
          return (
            <a
              key={index}
              href={node.href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              {renderInline(node.children)}
            </a>
          );
      }
    })}
  </>
);

const renderBlock = (block: BlockNode, index: number): JSX.Element => {
  switch (block.kind) {
    case "heading": {
      const Tag = HEADING_TAG[block.level];
      return (
        <Tag key={index} className={HEADING_CLASS[block.level]}>
          {renderInline(block.children)}
        </Tag>
      );
    }
    case "list": {
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag
          key={index}
          start={block.ordered ? block.start : undefined}
          className={`ml-5 list-outside space-y-1 ${block.ordered ? "list-decimal" : "list-disc"}`}
        >
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex}>{renderInline(item)}</li>
          ))}
        </ListTag>
      );
    }
    case "paragraph":
      return <p key={index}>{renderInline(block.children)}</p>;
  }
};

export interface FormattedTextProps {
  text: string;
  className?: string;
  style?: CSSProperties;
}

export const FormattedText = ({
  text,
  className,
  style,
}: FormattedTextProps): JSX.Element | null => {
  const blocks = useMemo(() => parseMarkup(text), [text]);
  if (blocks.length === 0) return null;

  return (
    <div className={`space-y-2 leading-relaxed ${className ?? ""}`} style={style}>
      {blocks.map(renderBlock)}
    </div>
  );
};
